import { FormEvent, useEffect, useState } from 'react'

import * as api from 'api/api'
import { AppSettings } from 'api/types'
import { useAuth } from 'auth/AuthContext'
import { SettingsTabs } from 'components/SettingsTabs'
import { Button, Card, ErrorState, SelectField, Spinner } from 'components/ui'
import { useAction, useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'
import { ForbiddenPage } from 'pages/system/ForbiddenPage'

/** The default theme/density new users (or any browser with no saved preference) see — people can still pick
 *  their own in Profile, which always overrides this (Figma: Admin / 32 · Settings — Appearance). */
export const AppearanceSettingsPage = () => {
  const { user } = useAuth()
  const { data: settings, error, loading, reload } = useAsync(api.fetchAppSettings, [])
  const { busy, run } = useAction()
  const [form, setForm] = useState<AppSettings | null>(null)

  useEffect(() => {
    if (settings && !form) setForm(settings)
  }, [settings, form])

  if (!user?.isAdmin) return <ForbiddenPage message={t('إعدادات المظهر متاحة لمدير النظام فقط.', 'Appearance settings are available to Admin only.')} />

  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (!form) return
    const done = await run('appearance', () => api.updateAppSettings(form), t('تم حفظ إعدادات المظهر', 'Appearance settings saved'))
    if (done) reload()
  }

  const organisationName = settings?.organisationName ?? 'JoPath'

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{t('الإعدادات', 'Settings')}</h1>
          <p className="muted">
            {t(`${organisationName} · التغييرات تُطبّق على كل مشروع وتُحفظ تلقائياً`, `${organisationName} · Changes apply to every project and are saved automatically`)}
          </p>
        </div>
      </div>

      <SettingsTabs />

      {loading ? (
        <Spinner />
      ) : error || !form ? (
        <ErrorState message={error ?? t('تعذر تحميل الإعدادات', 'Could not load settings')} onRetry={reload} />
      ) : (
        <Card title={t('المظهر والكثافة', 'Appearance and density')}>
          <p className="muted small">
            {t('القيم الافتراضية لكل مستخدم جديد، أو أي متصفح لم يُحدَّد فيه تفضيل شخصي بعد.', 'Defaults for every new user, or any browser that does not have a personal preference yet.')}
          </p>
          <form className="form-grid" onSubmit={save}>
            <SelectField label={t('المظهر الافتراضي', 'Default appearance')} value={form.defaultTheme} onChange={(event) => setForm({ ...form, defaultTheme: event.target.value })}>
              <option value="system">{t('حسب النظام', 'System')}</option>
              <option value="light">{t('فاتح', 'Light')}</option>
              <option value="dark">{t('داكن', 'Dark')}</option>
            </SelectField>
            <SelectField
              label={t('كثافة العرض الافتراضية', 'Default density')}
              value={form.defaultDensity}
              onChange={(event) => setForm({ ...form, defaultDensity: event.target.value })}
            >
              <option value="comfortable">{t('مريح', 'Comfortable')}</option>
              <option value="compact">{t('مضغوط', 'Compact')}</option>
            </SelectField>
            <div className="form-actions span-2">
              <Button type="submit" variant="primary" busy={busy === 'appearance'}>
                {t('حفظ', 'Save')}
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  )
}
